export type LaunchDatePreset = 'previous-year' | 'current-year' | 'next-year' | 'forward-12-months' | 'last-12-months' | 'custom';

export interface LaunchDateRange {
    startDate: string;
    endDate: string;
    preset: LaunchDatePreset;
}

export const formatDateInput = (date: Date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

export const parseDateInput = (value: string) => {
    const [year, month, day] = value.split('-').map(Number);
    return new Date(year, month - 1, day || 1, 12);
};

export const getLaunchDatePresetRange = (preset: Exclude<LaunchDatePreset, 'custom'>, today = new Date()): LaunchDateRange => {
    let start: Date;
    let end: Date;
    switch (preset) {
        case 'previous-year':
            start = new Date(today.getFullYear() - 1, 0, 1);
            end = new Date(today.getFullYear() - 1, 11, 31);
            break;
        case 'forward-12-months':
            start = new Date(today.getFullYear(), today.getMonth(), 1);
            end = new Date(today.getFullYear(), today.getMonth() + 12, 0);
            break;
        case 'next-year':
            start = new Date(today.getFullYear() + 1, 0, 1);
            end = new Date(today.getFullYear() + 1, 11, 31);
            break;
        case 'last-12-months':
            start = new Date(today.getFullYear(), today.getMonth() - 11, 1);
            end = new Date(today.getFullYear(), today.getMonth() + 1, 0);
            break;
        case 'current-year':
        default:
            start = new Date(today.getFullYear(), 0, 1);
            end = new Date(today.getFullYear(), 11, 31);
            break;
    }
    return { startDate: formatDateInput(start), endDate: formatDateInput(end), preset };
};

export const getMonthsInRange = (range: Pick<LaunchDateRange, 'startDate' | 'endDate'>) => {
    const start = parseDateInput(range.startDate);
    const end = parseDateInput(range.endDate);
    const months: Date[] = [];
    const cursor = new Date(start.getFullYear(), start.getMonth(), 1, 12);
    const last = new Date(end.getFullYear(), end.getMonth(), 1, 12);
    while (cursor <= last) {
        months.push(new Date(cursor));
        cursor.setMonth(cursor.getMonth() + 1);
    }
    return months;
};

export const formatLaunchDateRangeLabel = (range: Pick<LaunchDateRange, 'startDate' | 'endDate'>) => {
    const start = parseDateInput(range.startDate);
    const end = parseDateInput(range.endDate);
    const formatter = new Intl.DateTimeFormat(undefined, { month: 'short', year: 'numeric' });
    return `${formatter.format(start)} – ${formatter.format(end)}`;
};

export const isDateInLaunchRange = (date: Date, range: Pick<LaunchDateRange, 'startDate' | 'endDate'>) => {
    const value = new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
    return value >= parseDateInput(range.startDate).setHours(0, 0, 0, 0)
        && value <= parseDateInput(range.endDate).setHours(23, 59, 59, 999);
};
